import { useEffect, useContext, useState } from 'react'
import { MaintenanceListViewContext } from './Context'
import { MaintenanceFormModalContext } from '../Form/Context'
import { TableContext } from 'Context/Table'
import { useMaintenanceStore } from 'Store/Maintenance'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useAuthStore } from 'Store/Auth'
import ActionCell from 'Presentation/Components/Table/Components/CustomActionCell'
import moment from 'moment'
import 'moment-timezone'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'

const INITIAL_FILTER = {
  division: '',
  jobStatus: '',
  startPeriod: '',
}

const ViewModel = () => {
  const { data, getAll, setSelectedId, deleteMaintenance, clearState } = useMaintenanceStore(
    state => ({
      data: state.data,
      getAll: state.getAll,
      setSelectedId: state.setSelectedId,
      deleteMaintenance: state.delete,
      clearState: state.clearState,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const { toggleMaintenanceModal, setIsView } = useContext(MaintenanceListViewContext)
  const {
    // currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)
  const { updateFormType } = useContext(MaintenanceFormModalContext)
  const [filter, setFilter] = useState(INITIAL_FILTER)

  const userRoleId = profile?.roleId || 0

  const isHideWritingAction = [2, 15, 16, 20, 21].includes(userRoleId)

  const createMaintenance = () => {
    updateFormType('add')
    toggleMaintenanceModal(true)
    setIsView(false)
  }

  const setupTable = () => {
    updateSorting('id', true)
    updatePagination(true)
  }

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const submitFilter = async () => {
    await fetchData()
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    await getAll(INITIAL_FILTER)
  }

  const fetchData = async () => {
    try {
      updateLoading(true)
      await getAll({
        ...filter,
        startPeriod:
          filter.startPeriod.length > 0 ? moment(filter.startPeriod).format('YYYY-MM-DD') : '',
      })
      updateLoading(false)
      getAllSaleOrder({ hasProject: 1 })
    } catch (err: any) {
      updateLoading(false)
      updateError(err.message)
    }
  }

  const viewDetail = (id: number) => {
    updateFormType('edit')
    setSelectedId(id)
    toggleMaintenanceModal(true)
    setIsView(true)
  }

  const editDetail = (id: number) => {
    updateFormType('edit')
    setSelectedId(id)
    toggleMaintenanceModal(true)
    setIsView(false)
  }

  const askDeleteMaintenance = async (id: string) => {
    const { isConfirmed } = await Swal.fire({
      title: 'คุณต้องการที่จะลบการแจ้งหยุดงานนี้ใช่หรือไม่่?',
      text: 'การแจ้งหยุดงานที่ถูกลบไปแล้วจะไม่สามารถกู้คืนได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const { success, data } = await deleteMaintenance(parseInt(id))
      if (success) {
        toast.success('ลบการแจ้งหยุดงานสำเร็จ')
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'No.',
      accessor: 'id',
      width: 50,
      minWidth: 50,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'หน่วยงาน',
      accessor: 'division',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'วันที่เข้าไปทำ',
      accessor: 'problemReportAt',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid() ? moment(value).format('DD/MM/YY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'เวลาเริ่มทำงาน',
      accessor: 'startPeriod',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid()
              ? moment.utc(value).tz('Asia/Bangkok').format('HH:mm')
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'เวลาสิ้นสุดการทำงาน',
      accessor: 'endPeriod',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {value && moment(value).isValid()
              ? moment.utc(value).tz('Asia/Bangkok').format('HH:mm')
              : '-'}
          </span>
        )
      },
    },
    {
      Header: 'สถานะ',
      accessor: 'jobStatus',
      width: 220,
      minWidth: 220,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value ?? '-'}</span>
      },
    },
    {
      Header: 'ชนิดเครื่องจักร / ขนส่ง',
      accessor: 'machineTitle',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value ?? '-'}</span>
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='d-flex justify-content-end'>
          <ActionCell
            id={row.original.id}
            hideDuplicate
            hideEdit={isHideWritingAction}
            hideDelete={isHideWritingAction}
            onClickView={() => viewDetail(row.original.id)}
            onClickEdit={() => editDetail(row.original.id)}
            onClickDelete={() => askDeleteMaintenance(row.original.id)}
          />
        </div>
      ),
      disableSortBy: true,
    },
  ]

  // Initial Fetch and Initial Table Setup
  useEffect(() => {
    const action = async () => {
      setupTable()
      await fetchData()
    }

    action()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
      clearState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isHideWritingAction,
    data,
    TABLE_CONFIGS,
    createMaintenance,
    saleOrder,
    filter,
    onChangeFilter,
    submitFilter,
    clearFilter,
  }
}

export default ViewModel
